import { memo, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import type { CompanyNewsAnalysis } from './types';

interface RecommendationsProps {
  data: CompanyNewsAnalysis;
}

function deriveRecommendations(data: CompanyNewsAnalysis): string[] {
  const recs: string[] = [];
  const interp = data.investment_interpretation;

  // Primary source: justification split into actionable sentences
  if (interp?.justification) {
    const sentences = interp.justification
      .split(/\.\s+/)
      .map((s) => s.replace(/\.$/, '').trim())
      .filter((s) => s.length > 30 && s.length < 280);
    recs.push(...sentences.slice(0, 4));
  }

  // Supplement with top insight implications if we have fewer than 3 items
  if (recs.length < 3) {
    data.key_insights
      .filter((i) => !i.contrarian && i.implication && i.implication.length > 30)
      .slice(0, 4)
      .forEach((i) => {
        if (recs.length < 5 && i.implication) recs.push(i.implication);
      });
  }

  // Last resort: bearish factors as watch items
  if (recs.length < 2) {
    (interp?.bearish_factors ?? []).slice(0, 3).forEach((f) => {
      if (recs.length < 5) recs.push(f);
    });
  }

  return [...new Set(recs)].slice(0, 5);
}

function RecommendationsBase({ data }: RecommendationsProps) {
  const recs = useMemo(() => deriveRecommendations(data), [data]);

  if (recs.length === 0) return null;

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-neutral-900">Actionable Recommendations</h2>
        <p className="mt-0.5 text-xs text-neutral-500">
          Strategic priorities based on current news signals and market intelligence
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="divide-y divide-neutral-100">
          {recs.map((rec, i) => (
            <div key={i} className="flex items-start gap-4 px-5 py-4">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-bold text-primary-700">
                {i + 1}
              </span>
              <p className="text-sm leading-relaxed text-neutral-700">{rec}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 border-t border-neutral-100 bg-neutral-50/60 px-5 py-3 text-[11px] text-neutral-400">
          <Sparkles className="h-3 w-3" />
          AI-generated strategic recommendations based on news and market analysis
        </div>
      </div>
    </section>
  );
}

export const Recommendations = memo(RecommendationsBase);
